import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ArrowLeft, Download, Eye, FileText, X, ExternalLink } from "lucide-react";
import { api, apiError } from "@/lib/api";

export default function GymsPage() {
  const [gyms, setGyms] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => { (async () => {
    try { const r = await api.get("/gyms"); setGyms(r.data); } catch (e) { setError(apiError(e)); }
  })(); }, []);
  return <div className="content-frame" data-testid="gyms-page">
    <header className="page-heading">
      <div><p className="eyebrow">WBC GERMANY</p><h1>WBC Gyms</h1><p className="muted">Offizielle WBC-anerkannte Muay-Thai-Gyms in Deutschland.</p></div>
    </header>
    {error && <div className="form-error">{error}</div>}
    {!gyms.length && <div className="empty-state"><Building2 size={32} /><h2>Noch keine Gyms hinterlegt</h2><p>Sobald ein Admin ein Gym pflegt, erscheint es hier.</p></div>}
    <div className="gyms-grid" data-testid="gyms-list" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(220px, 1fr))",gap:20,marginTop:20}}>
      {gyms.map((g) => <Link key={g.id} to={`/gyms/${g.id}`} data-testid={`gym-tile-${g.id}`} style={{textDecoration:"none",color:"inherit",background:"#111",padding:20,borderRadius:8,textAlign:"center",transition:"transform .15s",cursor:"pointer"}} onMouseEnter={(e) => e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={(e) => e.currentTarget.style.transform="none"}>
        {g.logo_base64 ? <img src={g.logo_base64} alt={g.name} style={{width:"100%",maxWidth:180,height:180,objectFit:"contain",borderRadius:8,background:"#1a1a1a"}} data-testid={`gym-logo-${g.id}`} /> : <div style={{width:180,height:180,margin:"0 auto",background:"#1a1a1a",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",color:"#555"}}><Building2 size={56} /></div>}
        <b style={{display:"block",marginTop:12,fontSize:15,color:"#f4d33e"}} data-testid={`gym-name-${g.id}`}>{g.name}</b>
        <small className="muted">{g.location}</small>
      </Link>)}
    </div>
  </div>;
}

export function GymDetailPage() {
  const id = window.location.pathname.split("/").pop();
  const [gym, setGym] = useState(null);
  const [error, setError] = useState("");
  const [viewerUrl, setViewerUrl] = useState("");   // Blob-URL für Inline-Viewer
  const [busy, setBusy] = useState(false);
  useEffect(() => { (async () => {
    try { const r = await api.get(`/gyms/${id}`); setGym(r.data); } catch (e) { setError(apiError(e)); }
  })(); }, [id]);
  useEffect(() => () => { if (viewerUrl) URL.revokeObjectURL(viewerUrl); }, [viewerUrl]);

  // Ansehen: PDF als Blob laden und in einem Inline-Modal per <iframe> anzeigen.
  // Absicht: Umgeht den Popup-Blocker auf Mobile (window.open nach async fetch wird
  // dort geblockt) und funktioniert plattformübergreifend ohne Neuer-Tab-Genehmigung.
  const viewCert = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.get(`/gyms/${id}/certificate`, { responseType: "blob" });
      // Content-Type vom Server ist "application/pdf"; wir zwingen den Typ zusätzlich
      // auf der Client-Seite, damit iframe/browser die PDF sicher rendern.
      const pdfBlob = r.data.type === "application/pdf" ? r.data : new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(pdfBlob);
      setViewerUrl(url);
    } catch (e) { setError(apiError(e)); }
    finally { setBusy(false); }
  };

  const downloadCert = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.get(`/gyms/${id}/certificate/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = gym.certificate_file_name || `${gym.name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setError(apiError(e)); }
    finally { setBusy(false); }
  };

  const closeViewer = () => {
    if (viewerUrl) URL.revokeObjectURL(viewerUrl);
    setViewerUrl("");
  };

  if (error && !gym) return <div className="content-frame"><div className="form-error">{error}</div></div>;
  if (!gym) return <div className="content-frame"><p className="muted">Wird geladen…</p></div>;

  return <div className="content-frame" data-testid="gym-detail-page">
    <header className="page-heading"><div><p className="eyebrow">WBC GYM</p><h1 data-testid="gym-detail-name">{gym.name}</h1><p className="muted" data-testid="gym-detail-location">{gym.location}</p></div><Link to="/gyms" className="button ghost" data-testid="gym-back-button"><ArrowLeft size={17} /> Zurück</Link></header>
    {error && <div className="form-error" data-testid="gym-cert-error" style={{marginTop:12}}>{error}</div>}
    <section style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24,marginTop:24}}>
      <div style={{background:"#111",padding:20,borderRadius:8}}>
        {gym.logo_base64 ? <img src={gym.logo_base64} alt={gym.name} style={{width:"100%",maxHeight:400,objectFit:"contain"}} data-testid="gym-detail-logo" /> : <div style={{padding:60,textAlign:"center",color:"#555"}}><Building2 size={80} /></div>}
      </div>
      <div style={{background:"#111",padding:20,borderRadius:8}}>
        <h2 style={{color:"#f4d33e",fontSize:14,letterSpacing:0.6,marginTop:0}}>WBC-URKUNDE</h2>
        {gym.has_certificate ? <div>
          <p style={{fontSize:13,color:"#a89a70"}}><FileText size={16} style={{verticalAlign:"middle"}} /> {gym.certificate_file_name} · {(gym.certificate_size_bytes/1024/1024).toFixed(2)} MB</p>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <button className="button primary" onClick={viewCert} disabled={busy} data-testid="gym-cert-view"><Eye size={16} /> Ansehen</button>
            <button className="button ghost" onClick={downloadCert} disabled={busy} data-testid="gym-cert-download"><Download size={16} /> Download</button>
          </div>
        </div> : <p className="muted">Noch keine Urkunde hinterlegt.</p>}
      </div>
    </section>

    {/* Inline-Viewer als Modal — funktioniert auf Desktop und Mobile ohne Popup-Blocker. */}
    {viewerUrl && <div data-testid="gym-cert-viewer" role="dialog" aria-modal="true" onClick={closeViewer}
        style={{position:"fixed",inset:0,background:"#000",zIndex:2000,display:"flex",flexDirection:"column"}}>
      <div onClick={(e) => e.stopPropagation()} style={{background:"#0a0a0a",padding:"10px 14px",display:"flex",alignItems:"center",gap:12,borderBottom:"1px solid #222"}}>
        <b style={{color:"#f4d33e",fontSize:14,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{gym.certificate_file_name}</b>
        <a href={viewerUrl} target="_blank" rel="noopener noreferrer" className="button ghost" data-testid="gym-cert-viewer-external" style={{fontSize:12}}><ExternalLink size={14} /> Neuer Tab</a>
        <button type="button" className="button" onClick={closeViewer} data-testid="gym-cert-viewer-close" style={{background:"#8a2a2a",color:"#fff",border:"none",fontSize:12}}><X size={14} /> Schließen</button>
      </div>
      <iframe src={viewerUrl} title="WBC-Urkunde" data-testid="gym-cert-viewer-frame" style={{flex:1,width:"100%",border:0,background:"#111"}} />
      <noscript>
        <a href={viewerUrl} target="_blank" rel="noopener noreferrer">Urkunde in neuem Tab öffnen</a>
      </noscript>
    </div>}
  </div>;
}
